import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getRewardAddresses,
  updateRewardAddresses,
  type RewardAddresses,
} from '../api/settings';

const COREUM_ADDRESS_REGEX = /^core1[a-z0-9]{38,58}$/;

/**
 * Hook to manage reward addresses for claim payouts
 */
export function useRewardAddresses() {
  const queryClient = useQueryClient();

  const {
    data,
    isPending: isLoading,
    error,
  } = useQuery({
    queryKey: ['reward-addresses'],
    queryFn: getRewardAddresses,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const [localAddresses, setLocalAddresses] = useState<RewardAddresses>({});
  const [isDirty, setIsDirty] = useState(false);

  // Sync local state when data loads
  useEffect(() => {
    if (data && !isDirty) {
      setLocalAddresses(data.reward_addresses || {});
    }
  }, [data, isDirty]);

  const updateAddress = useCallback((type: string, value: string) => {
    setLocalAddresses(prev => ({ ...prev, [type]: value }));
    setIsDirty(true);
  }, []);

  const resetAddresses = useCallback(() => {
    if (data) {
      setLocalAddresses(data.reward_addresses || {});
    }
    setIsDirty(false);
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: updateRewardAddresses,
    onSuccess: (result) => {
      queryClient.setQueryData(['reward-addresses'], result);
      // Also update the main settings cache
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      setIsDirty(false);
    },
  });

  const saveAddresses = useCallback(() => {
    saveMutation.mutate(localAddresses);
  }, [saveMutation, localAddresses]);

  const validateCoreum = useCallback((address: string): string | null => {
    if (!address || address.trim() === '') return null;
    if (!COREUM_ADDRESS_REGEX.test(address)) {
      return 'Invalid Coreum address. Must start with core1';
    }
    return null;
  }, []);

  return {
    addresses: localAddresses,
    isLoading,
    isDirty,
    error,
    isUpdating: saveMutation.isPending,
    updateError: saveMutation.error,
    isUpdateSuccess: saveMutation.isSuccess,
    updateAddress,
    saveAddresses,
    resetAddresses,
    validateCoreum,
  };
}
